import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import Layout from "../homeDashboard/HomeDashboardLayout";
import "../dashboard/Dashboard.css";
import profileAvatar from "../assets/images/c1.png";
import bgImage from "../assets/images/video-img.png";
import useProfileSettingsProfile from "../hooks/useProfileSettingsProfile";
import usePreferredLanguageToggle from "../hooks/usePreferredLanguageToggle";
import { updateProfileSettingsProfile } from "../services/profileSettingsService";
//Import SVg
import { LanguageIconBlack, BackSVG } from "../components/SvgIcons";
import MyWalletSidebar from "../homeDashboard/MyWalletSidebar";

const INDIA_FLAG = String.fromCodePoint(0x1f1ee, 0x1f1f3);
const profileSections = [
  {
    title: "Basic Account Details",
    items: [
      {
        id: "fullName",
        label: "Full Name",
        type: "text",
        placeholder: "Enter Full Name",
      },
      {
        id: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter Email",
      },
      {
        id: "phoneNumber",
        label: "Phone Number",
        type: "phone",
        placeholder: "Enter Phone Number",
      },
    ],
  },
  {
    title: "Personal Information",
    items: [
      {
        id: "whatsapp",
        label: "WhatsApp",
        type: "phone",
        placeholder: "Enter WhatsApp Number",
      },
      {
        id: "altMobile",
        label: "Alt Mobile",
        type: "phone",
        placeholder: "Enter Alternate Mobile",
      },
      {
        id: "altEmail",
        label: "Alt Email",
        type: "email",
        placeholder: "Alt Email",
      },
    ],
  },
  {
    title: "Education & Qualification",
    items: [
      {
        id: "qualification",
        label: "Education & Qualification",
        placeholder: "Select Qualification",
        type: "qualification",
        options: [
          "Middle School",
          "Matriculation",
          "10+2",
          "Graduate",
          "Post Graduate",
          "PhD",
        ],
      },
    ],
  },
  {
    title: "Address",
    items: [
      {
        id: "state",
        label: "States",
        type: "state",
        placeholder: "Select State",
        options: [],
      },
      {
        id: "city",
        label: "City",
        type: "text",
        placeholder: "Enter City",
      },
      {
        id: "pinCode",
        label: "Pin Code",
        type: "text",
        placeholder: "Enter Pin Code",
      },
      {
        id: "address",
        label: "Address",
        type: "textarea",
        placeholder: "Address",
      },
    ],
  },
  {
    title: "Other Details",
    items: [
      {
        id: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        placeholder: "Date of Birth",
      },
      {
        id: "gender",
        label: "Gender",
        type: "gender",
        options: ["Male", "Female", "Others"],
      },
    ],
  },
];
const EditProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const qualificationRef = useRef(null);
  const genderRef = useRef(null);
  const stateRef = useRef(null);
  const profilePictureInputRef = useRef(null);
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useProfileSettingsProfile();
  const [openDropdown, setOpenDropdown] = useState("");
  const [selectedProfilePicture, setSelectedProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    whatsapp: "",
    altMobile: "",
    altEmail: "",
    qualification: [],
    state: "",
    city: "",
    pinCode: "",
    address: "",
    dateOfBirth: "",
    gender: "",
    countryCode: "+91",
    stateId: "",
    qualificationIds: [],
  });

  const qualificationOptions = useMemo(
    () =>
      Array.isArray(profile?.qualifications) ? profile.qualifications : [],
    [profile?.qualifications],
  );

  const stateOptions = useMemo(
    () => (Array.isArray(profile?.states) ? profile.states : []),
    [profile?.states],
  );

  const activeProfileImage =
    profilePicturePreview || profile?.profile_pics || profileAvatar;
  const {
    currentPreferredLanguage,
    handleLanguageToggle,
    isPending: isLanguageUpdatePending,
  } = usePreferredLanguageToggle(profile);

  const dynamicProfileSections = useMemo(
    () =>
      profileSections.map((section) => ({
        ...section,
        items: section.items.map((item) => {
          if (item.type === "qualification") {
            return {
              ...item,
              options: qualificationOptions
                .map((qualification) => qualification?.name)
                .filter(Boolean),
            };
          }

          if (item.type === "state") {
            return {
              ...item,
              options: stateOptions
                .map((state) => state?.full_name)
                .filter(Boolean),
            };
          }

          return item;
        }),
      })),
    [qualificationOptions, stateOptions],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid image",
        text: "Please choose a JPG or PNG image.",
        confirmButtonColor: "#6c4de6",
      });
      event.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      await Swal.fire({
        icon: "warning",
        title: "Image too large",
        text: "Profile image must be 2MB or smaller.",
        confirmButtonColor: "#6c4de6",
      });
      event.target.value = "";
      return;
    }

    setSelectedProfilePicture(file);
    setProfilePicturePreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }

      return URL.createObjectURL(file);
    });
  };

  const handleRemoveSelectedProfilePicture = () => {
    setSelectedProfilePicture(null);
    setProfilePicturePreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }

      return "";
    });

    if (profilePictureInputRef.current) {
      profilePictureInputRef.current.value = "";
    }
  };

  const updateProfileMutation = useMutation({
    mutationFn: updateProfileSettingsProfile,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ["profileSettingsProfile"],
      });
      await Swal.fire({
        icon: "success",
        title: "Profile updated",
        text:
          result?.meta?.message ||
          "Your profile has been updated successfully.",
        confirmButtonColor: "#6c4de6",
      });
    },
    onError: async (mutationError) => {
      await Swal.fire({
        icon: "error",
        title: "Update failed",
        text: mutationError?.message || "Unable to update profile right now.",
        confirmButtonColor: "#6c4de6",
      });
    },
  });

  const handleSave = async () => {
    const trimmedName = String(formData.fullName || "").trim();
    const trimmedEmail = String(formData.email || "")
      .trim()
      .toLowerCase();
    const trimmedPhone = String(formData.phoneNumber || "").trim();
    const trimmedWhatsapp = String(formData.whatsapp || "").trim();
    const trimmedAltMobile = String(formData.altMobile || "").trim();
    const trimmedAltEmail = String(formData.altEmail || "")
      .trim()
      .toLowerCase();
    const trimmedCity = String(formData.city || "").trim();
    const trimmedPinCode = String(formData.pinCode || "").trim();
    const trimmedAddress = String(formData.address || "").trim();
    const normalizedDateOfBirth = String(formData.dateOfBirth || "").trim();

    if (!trimmedName) {
      await Swal.fire({
        icon: "warning",
        title: "Full name required",
        text: "Please enter your full name.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail)) {
      await Swal.fire({
        icon: "warning",
        title: "Valid email required",
        text: "Please enter a valid email address.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    if (trimmedPhone && !/^\d{10,15}$/.test(trimmedPhone)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid phone number",
        text: "Phone number must be 10 to 15 digits.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    if (trimmedWhatsapp && !/^\d{10,15}$/.test(trimmedWhatsapp)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid WhatsApp number",
        text: "WhatsApp number must be 10 to 15 digits.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    if (trimmedAltMobile && !/^\d{10,15}$/.test(trimmedAltMobile)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid alternate mobile",
        text: "Alternate mobile number must be 10 to 15 digits.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    if (trimmedAltEmail && !/\S+@\S+\.\S+/.test(trimmedAltEmail)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid alternate email",
        text: "Please enter a valid alternate email address.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    const genderMap = {
      Male: "M",
      Female: "F",
      Others: "O",
    };

    if (
      normalizedDateOfBirth &&
      !/^\d{4}-\d{2}-\d{2}$/.test(normalizedDateOfBirth)
    ) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid date of birth",
        text: "Date of birth must be in YYYY-MM-DD format.",
        confirmButtonColor: "#6c4de6",
      });
      return;
    }

    const requestPayload = new FormData();
    requestPayload.append("title", profile?.title || "Mr.");
    requestPayload.append("name", trimmedName);
    requestPayload.append("email", trimmedEmail);
    requestPayload.append("country_code", formData.countryCode || "+91");
    requestPayload.append("phone", trimmedPhone);
    requestPayload.append("whatsapp_mobile", trimmedWhatsapp);
    requestPayload.append("alt_mobile", trimmedAltMobile);
    requestPayload.append("alt_email", trimmedAltEmail);
    requestPayload.append(
      "state_id",
      formData.stateId ? String(formData.stateId) : "",
    );
    requestPayload.append("city", trimmedCity);
    requestPayload.append("pin_code", trimmedPinCode);
    requestPayload.append("address", trimmedAddress);
    requestPayload.append("date_of_birth", normalizedDateOfBirth);
    requestPayload.append("gender", genderMap[formData.gender] || "O");
    requestPayload.append(
      "selected_qualification_id",
      JSON.stringify(
        Array.isArray(formData.qualificationIds)
          ? formData.qualificationIds
          : [],
      ),
    );

    if (selectedProfilePicture) {
      requestPayload.append("profile_picture", selectedProfilePicture);
    }

    await updateProfileMutation.mutateAsync(requestPayload);
  };

  useEffect(() => {
    if (!profile) {
      return;
    }

    const selectedQualifications = Array.isArray(profile?.user_qualification)
      ? profile.user_qualification
      : [];

    const normalizedQualifications = selectedQualifications
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (typeof item === "number") {
          return (
            qualificationOptions.find(
              (qualification) => qualification?.id === item,
            )?.name || ""
          );
        }

        return item?.name || "";
      })
      .filter(Boolean);

    const normalizedQualificationIds = selectedQualifications
      .map((item) => {
        if (typeof item === "number") {
          return item;
        }

        if (typeof item === "object" && item?.id) {
          return item.id;
        }

        if (typeof item === "string") {
          return (
            qualificationOptions.find(
              (qualification) => qualification?.name === item,
            )?.id || null
          );
        }

        return null;
      })
      .filter(Boolean);

    const genderMap = {
      M: "Male",
      F: "Female",
      O: "Others",
    };

    const normalizedDialCode =
      String(profile?.country_code || "+91")
        .replace("+", "")
        .trim() || "91";
    const stripCountryCode = (value) => {
      const rawValue = String(value || "").trim();
      if (!rawValue) {
        return "";
      }

      const compactValue = rawValue.replace(/^\+/, "");
      return compactValue.startsWith(normalizedDialCode)
        ? compactValue.slice(normalizedDialCode.length)
        : rawValue;
    };

    setFormData({
      fullName: profile?.name || "",
      email: profile?.email || "",
      phoneNumber: stripCountryCode(profile?.phone),
      whatsapp: stripCountryCode(profile?.whatsapp_mobile),
      altMobile: stripCountryCode(profile?.alt_mobile),
      altEmail: profile?.alt_email || "",
      qualification: normalizedQualifications,
      state: profile?.state_name || "",
      city: profile?.city || "",
      pinCode: profile?.pin_code || "",
      address: profile?.address || "",
      dateOfBirth: profile?.date_of_birth || "",
      gender: genderMap[profile?.gender] || profile?.gender || "",
      countryCode: profile?.country_code || "+91",
      stateId: profile?.state_id || "",
      qualificationIds: normalizedQualificationIds,
    });
  }, [profile, qualificationOptions]);

  useEffect(() => {
    return () => {
      if (profilePicturePreview) {
        URL.revokeObjectURL(profilePicturePreview);
      }
    };
  }, [profilePicturePreview]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideQualification =
        qualificationRef.current &&
        qualificationRef.current.contains(event.target);
      const clickedInsideGender =
        genderRef.current && genderRef.current.contains(event.target);
      const clickedInsideState =
        stateRef.current && stateRef.current.contains(event.target);

      if (
        !clickedInsideQualification &&
        !clickedInsideGender &&
        !clickedInsideState
      ) {
        setOpenDropdown("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const renderFieldControl = (item) => {
    if (item.type === "phone") {
      return (
        <div className="ep-phone-row">
          <div className="ep-phone-code">
            <span className="ep-phone-flag" aria-hidden="true">
              {INDIA_FLAG}
            </span>
            <span className="ep-phone-code__text">+91</span>
          </div>

          <input
            id={item.id}
            name={item.id}
            className="ep-input ep-input--phone"
            type="tel"
            value={formData[item.id]}
            placeholder={item.placeholder}
            onChange={handleChange}
          />
        </div>
      );
    }

    if (item.type === "qualification") {
      const isOpen = openDropdown === item.id;
      const selectedQualifications = formData[item.id] || [];
      const currentLabel = item.placeholder || "Select Qualification";

      return (
        <div
          className="ep-select-wrap ep-select-wrap--qualification"
          ref={qualificationRef}
        >
          <button
            type="button"
            className="ep-input ep-select ep-select--button ep-select--multi"
            onClick={() =>
              setOpenDropdown((prev) => (prev === item.id ? "" : item.id))
            }
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {selectedQualifications.length ? (
              <div
                className="ep-select__chips"
                aria-label="Selected qualifications"
              >
                {selectedQualifications.map((qualification) => (
                  <span key={qualification} className="ep-select__chip">
                    {qualification}
                  </span>
                ))}
              </div>
            ) : (
              <span className="ep-select__placeholder">{currentLabel}</span>
            )}
          </button>

          {isOpen ? (
            <div
              className="ep-dropdown ep-dropdown--qualification"
              role="listbox"
              aria-label={item.label}
            >
              {item.options.map((option) => {
                const isChecked = selectedQualifications.includes(option);

                return (
                  <button
                    type="button"
                    key={option}
                    className={`ep-dropdown__item ep-dropdown__item--qualification${isChecked ? " is-selected" : ""}`}
                    onClick={() => {
                      setFormData((prev) => {
                        const nextValues = isChecked
                          ? (Array.isArray(prev[item.id])
                              ? prev[item.id]
                              : []
                            ).filter((value) => value !== option)
                          : [...(prev[item.id] || []), option];

                        const nextIds = qualificationOptions
                          .filter((qualification) =>
                            nextValues.includes(qualification?.name),
                          )
                          .map((qualification) => qualification?.id)
                          .filter(Boolean);

                        return {
                          ...prev,
                          [item.id]: nextValues,
                          qualificationIds: nextIds,
                        };
                      });
                    }}
                  >
                    <span className="ep-dropdown__check" aria-hidden="true" />
                    <span className="ep-dropdown__text">{option}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "state") {
      const isOpen = openDropdown === item.id;
      const currentLabel =
        formData[item.id] || item.placeholder || "Select State";

      return (
        <div className="ep-select-wrap ep-select-wrap--state" ref={stateRef}>
          <button
            type="button"
            className="ep-input ep-select ep-select--button"
            onClick={() =>
              setOpenDropdown((prev) => (prev === item.id ? "" : item.id))
            }
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className={formData[item.id] ? "" : "ep-select__placeholder"}>
              {currentLabel}
            </span>
          </button>

          {isOpen ? (
            <div
              className="ep-dropdown ep-dropdown--state"
              role="listbox"
              aria-label={item.label}
            >
              {item.options.map((option) => {
                const isSelected = formData[item.id] === option;

                return (
                  <button
                    type="button"
                    key={option}
                    className={`ep-dropdown__item ep-dropdown__item--state${isSelected ? " is-selected" : ""}`}
                    onClick={() => {
                      const matchedState = stateOptions.find(
                        (state) => state?.full_name === option,
                      );

                      setFormData((prev) => ({
                        ...prev,
                        [item.id]: option,
                        stateId: matchedState?.id || "",
                      }));
                      setOpenDropdown("");
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "select") {
      return (
        <div className="ep-select-wrap">
          <select
            id={item.id}
            name={item.id}
            className="ep-input ep-select"
            value={formData[item.id] || ""}
            onChange={handleChange}
          >
            {item.options.map((option, optionIndex) => (
              <option
                key={option}
                value={optionIndex === 0 ? "" : option}
                disabled={optionIndex === 0}
              >
                {option}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (item.type === "gender") {
      const isOpen = openDropdown === item.id;
      const currentLabel =
        formData[item.id] || item.placeholder || "Select Gender";

      return (
        <div className="ep-select-wrap ep-select-wrap--gender" ref={genderRef}>
          <button
            type="button"
            className="ep-input ep-select ep-select--button"
            onClick={() =>
              setOpenDropdown((prev) => (prev === item.id ? "" : item.id))
            }
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className={formData[item.id] ? "" : "ep-select__placeholder"}>
              {currentLabel}
            </span>
          </button>

          {isOpen ? (
            <div
              className="ep-dropdown ep-dropdown--gender"
              role="listbox"
              aria-label={item.label}
            >
              {item.options.map((option) => {
                const isSelected = formData[item.id] === option;

                return (
                  <button
                    type="button"
                    key={option}
                    className={`ep-dropdown__item ep-dropdown__item--gender${isSelected ? " is-selected" : ""}`}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        [item.id]: option,
                      }));
                      setOpenDropdown("");
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "textarea") {
      return (
        <textarea
          id={item.id}
          name={item.id}
          className="ep-input ep-textarea"
          value={formData[item.id] || ""}
          onChange={handleChange}
          placeholder={item.placeholder}
          rows="3"
        />
      );
    }

    if (item.type === "date") {
      return (
        <div className="ep-date-wrap">
          <input
            id={item.id}
            name={item.id}
            className="ep-input"
            type="date"
            value={formData[item.id] || ""}
            onChange={handleChange}
          />
        </div>
      );
    }

    return (
      <input
        id={item.id}
        name={item.id}
        className="ep-input"
        type={item.type || "text"}
        value={formData[item.id] || ""}
        placeholder={item.placeholder}
        onChange={handleChange}
      />
    );
  };

  if (isLoading) {
    return (
      <Layout showHeader={false}>
        <div className="setting-profile-page">
          <div className="setting-profile-shell">
            <div className="setting-profile-content">
              <p className="mb-0">Loading profile...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout showHeader={false}>
        <div className="setting-profile-page">
          <div className="setting-profile-shell">
            <div className="setting-profile-content">
              <p className="mb-0">
                Unable to load profile: {error?.message || "Unknown error"}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <style>{`
        .page-content {
          background:
            radial-gradient(circle at top left, rgba(103, 78, 221, 0.10), transparent 28%),
            linear-gradient(180deg, #f6f3ff 0%, #ffffff 26%);
        }

        body {
          background: #f6f3ff !important;
        }

        .setting-profile-page {
          min-height: 100%;
          padding: 0px;
          overflow-x: clip;
        }

        .setting-profile-shell .vp-hero {
          position: relative;
          min-height: 280px;
          border-radius: 34px;
          overflow: visible;
          box-shadow: 0 26px 60px rgba(63, 39, 151, 0.14);
        }

        .setting-profile-shell .vp-hero__bg {
          background-position: center;
          background-size: cover;
          filter: saturate(0.9);
          transform: scale(1.02);
        }

        .setting-profile-shell .vp-hero__overlay {
          border-radius: 34px;
          background: linear-gradient(180deg, rgba(16, 10, 40, 0.12) 0%, rgba(16, 10, 40, 0.38) 100%);
        }
          .setting-profile-shell .setting-profile-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          width: 100%;
          margin: 24px 0 0;
          padding: 0 4px 28px;
          background: transparent;
        }

        .setting-profile-shell .setting-profile-save {
          width: auto;
          min-width: 220px;
          min-height: 54px;
          padding: 0 28px;
          border: 0;
          border-radius: 999px;
          background: #6043ee;
          color: #ffffff;
          font-weight: 700;
          box-shadow: 0 10px 22px rgba(103, 78, 221, 0.18);
          margin-bottom:14px;
        }

        .setting-profile-shell .setting-profile-save:hover:not(:disabled) {
          background: #5134da;
        }

        .setting-profile-shell .setting-profile-save:disabled {
          opacity: 0.65;
          cursor: wait;
        }
        .setting-profile-shell .vp-nav-btn {
          width: 42px;
          height: 42px;
          border: 0;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(12px);
          box-shadow: 0 10px 25px rgba(24, 18, 55, 0.14);
          transition: transform 0.2s ease, background-color 0.2s ease;
        }

        .setting-profile-shell .vp-nav-btn:hover {
          transform: translateY(-1px);
          background: rgba(255, 255, 255, 0.26);
        }

        .setting-profile-shell .vp-nav-btn--language {
          transition: transform 0.25s ease, background-color 0.2s ease;
        }

        .setting-profile-shell .vp-nav-btn--language.is-hi {
          transform: rotate(180deg);
        }

        .setting-profile-shell .vp-nav-btn--language.is-loading {
          animation: setting-profile-language-spin 0.8s linear infinite;
        }

        @keyframes setting-profile-language-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .setting-profile-shell .vp-nav-btn--back {
          left: 18px;
          top: 18px;
        }

        .setting-profile-shell .vp-hero__actions {
          top: 18px;
          right: 18px;
        }

        .setting-profile-shell .vp-avatar-wrap {
          left: 32px;
          bottom: -46px;
          width: 110px;
          height: 110px;
          padding: 5px;
          border-radius: 50%;
          background: linear-gradient(180deg, #ffffff 0%, #f1ebff 100%);
          box-shadow: 0 18px 34px rgba(50, 31, 124, 0.16);
        }

        .setting-profile-shell .vp-avatar {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }

        .setting-profile-shell .vp-avatar-edit {
          position: absolute;
          right: -4px;
          bottom: -4px;
          width: 36px;
          height: 36px;
          border: 0;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #7d62ff 0%, #5d43dd 100%);
          color: #ffffff;
          box-shadow: 0 10px 22px rgba(84, 59, 201, 0.3);
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .setting-profile-shell .vp-avatar-edit:hover {
          transform: translateY(-1px);
        }

        .setting-profile-shell .vp-avatar-remove {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 32px;
          height: 32px;
          border: 0;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          color: #5d43dd;
          box-shadow: 0 10px 22px rgba(84, 59, 201, 0.18);
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .setting-profile-shell .vp-avatar-remove:hover {
          transform: translateY(-1px);
        }

        .setting-profile-shell .vp-avatar-status {
          position: absolute;
          left: 50%;
          bottom: -18px;
          transform: translateX(-50%);
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(29, 22, 57, 0.9);
          color: #ffffff;
          font-size: 0.72rem;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
          box-shadow: 0 8px 18px rgba(24, 18, 55, 0.18);
        }

        .setting-profile-content {
          margin-top: 22px;
        }

        .setting-profile-summary {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 0px;
        }

        .setting-profile-summary__copy {
          min-width: 0;
          flex: 1 1 auto;
        }

        .setting-profile-shell .vp-name {
          margin: 0;
          font-size: 2rem;
          font-weight: 800;
          line-height: 1.08;
          color: #181329;
        }

        .setting-profile-shell .vp-email {
          margin: 6px 0 10px;
          font-size: 1rem;
          font-weight: 700;
          overflow-wrap: anywhere;
        }

        .setting-profile-shell .update-inffo {
          max-width: 560px;
          font-size: 0.98rem;
          line-height: 1.6;
          color: #78728f;
          overflow-wrap: anywhere;
        }

        .setting-profile-shell .vp-location-row {
          margin: 0;
          min-width: 0;
        }

        .setting-profile-shell .vp-location-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 12px 16px;
          border-radius: 999px;
          background: #f6f2ff;
          border: 1px solid #e1d8ff;
          color: #241b43;
          font-weight: 700;
          max-width: 100%;
          overflow-wrap: anywhere;
        }

        .setting-profile-shell .ep-section {
          display: block;
        }

        .setting-profile-shell .ep-group {
          min-width: 0;
          height: 100%;
        }

        .setting-profile-shell .ep-group--full {
          grid-column: 1 / -1;
        }

        .setting-profile-shell .ep-section__title {
             margin: 5px 0 10px;
             font-size: 1.18rem;
             font-weight: 800;
             color: #19142a;
             border-top: 1px solid #d9d9d9;
             padding-top: 20px;
        }

        .setting-profile-shell .ep-field {
          min-width: 0;
          margin-bottom: 14px;
        }

        .setting-profile-shell .ep-field__label {
          margin-bottom: 8px;
          color: #6f6887;
          font-size: 0.92rem;
          font-weight: 700;
        }

        .setting-profile-shell .ep-input,
        .setting-profile-shell .ep-phone-code {
          border-radius: 12px;
        }

        .setting-profile-shell .ep-input {
          width: 100%;
          padding: 14px 16px;
          border: 1.5px solid #e2dbfa;
          background: #fbfaff;
          color: #1c1730;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }

        .setting-profile-shell .ep-input:focus,
        .setting-profile-shell .ep-select--button:focus {
          border-color: #7b5cff;
          box-shadow: 0 0 0 4px rgba(123, 92, 255, 0.10);
          background: #ffffff;
          outline: none;
        }

        .setting-profile-shell .ep-input::placeholder,
        .setting-profile-shell .ep-select__placeholder {
          color: #9892aa;
        }

        .setting-profile-shell .ep-phone-row {
          gap: 10px;
        }

        .setting-profile-shell .ep-input--phone {
          flex: 1 1 auto;
          min-width: 0;
        }

        .setting-profile-shell .ep-phone-row {
          display: flex;
          align-items: center;
          min-width: 0;
        }

        .setting-profile-shell .ep-phone-code {
          min-width: 72px;
          border: 1.5px solid #e2dbfa;
          background: #fbfaff;
          color: #2d244d;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .setting-profile-shell .ep-phone-code__text {
          font-size: 0.95rem;
          font-weight: 700;
          line-height: 1;
          color: #2d244d;
        }

        .setting-profile-shell .ep-select-wrap::after {
          right: 18px;
        }

        .setting-profile-shell .ep-select__chips {
          min-width: 0;
        }

        .setting-profile-shell .ep-dropdown {
          margin-top: 8px;
          max-width: 100%;
          border-radius: 18px;
          border: 1px solid #e4ddfb;
          box-shadow: 0 16px 30px rgba(55, 40, 133, 0.10);
        }

        .setting-profile-shell .ep-grid-row {
          gap: 14px;
        }

        .setting-profile-shell .vp-logout-wrap {
          position: static !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
          width: auto !important;
          margin-left: 0 !important;
          padding-top: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          display: flex;
          justify-content: flex-start;
          margin-top: 28px;
        }

        .setting-profile-shell .vp-logout-btn {
          min-height: 48px;
          border: 0;
          border-radius: 999px;
          font-size: 1rem;
          font-weight: 800;
          box-shadow: 0 18px 28px rgba(103, 78, 221, 0.22);
          padding:0 25px;
        }
      .formContainer:has(.ep-field:only-child) {
        grid-template-columns: repeat(1, minmax(0, 1fr));
      }

      .formContainer:has(.ep-field:only-child) .ep-field {
        width: 100%;
      }

      .setting-profile-shell .ep-group--full .formContainer .ep-field:last-child {
          grid-column: 1 / -1;
          width: 100%;
      }
        
      .setting-profile-shell .ep-section > .ep-group:last-child
          .formContainer {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
      }
      .setting-profile-shell .vp-logout-wrap {
          margin-top: -62px !important;
          margin-bottom: 20px;
          display: flex;
          justify-content:right;
          padding-right: 0;
      }
    .setting-profile-shell .vp-logout-btn {
      width:auto;
    }
    .formContainer.other-details-container{
      align-items:flex-end;
    }
    @media (max-width: 1199px) {
      .setting-profile-page {
        padding:0px;
      }
      .setting-profile-shell .vp-hero {
        min-height: 252px;
      }

      .setting-profile-content {
        padding: 68px 26px 28px;
      }

      .setting-profile-shell .ep-group {
        padding: 0px 0px 0px;
      }
      .vp-user {
         margin-bottom: 0;
       }
    .inner-wallet {
         padding: 0 25px;
      }
   }
    @media (min-width: 1200px) and (max-width : 1400px){
      .setting-profile-shell .setting-profile-save {
      font-size: 14px;
      min-width: 165px;
      min-height: 50px;
    }
    .setting-profile-shell .update-inffo {
       font-size: 14px;
    }
    .setting-profile-shell .vp-name {
       font-size: 30px;
    }
    .setting-profile-shell .vp-location-chip {
      gap: 4px;
      padding: 12px 9px;
      font-size: 13px;
    }
  }
  @media (min-width: 768px) and (max-width : 1199px){
    .setting-profile-shell .vp-logout-wrap {
      margin-top: -82px !important;
    }
  }
  @media (max-width: 991px) {
    .setting-profile-page {
      padding: 0px;
    }

    .setting-profile-shell .vp-hero {
      min-height: 236px;
    }

    .setting-profile-content {
      padding: 64px 20px 24px;
    }

    .setting-profile-summary {
      flex-direction: column;
      align-items: flex-start;
    }

    .setting-profile-shell .vp-location-row {
      width: 100%;
    }

    .setting-profile-shell .ep-section {
      grid-template-columns: 1fr;
    }

    .setting-profile-shell .ep-group,
    .setting-profile-shell .ep-group--full {
      grid-column: auto;
    }
  }
  @media (max-width: 1600px){
    .formContainer {
      gap: 8px;
      }
     .setting-profile-shell .ep-input {
    padding: 10px 10px;
      }
  }
      

  @media  (min-width: 576px) and (max-width: 991px){
    .ep-input {
    font-size: 13px;
      }
  }
  @media (max-width: 767px) {
    .headerDash {
      display: none;
    }

    .layout-wrapper .main-content {
      padding-top: 0px !important;
  }
.setting-profile-shell .setting-profile-save {
  min-width: 180px;
    min-height: 40px;
    font-size: 14px;
}
.setting-profile-page {
  padding:0px;
}
.setting-profile-shell .vp-hero {
  min-height: 196px;
  border-radius: 24px;
}
.vp-user {
    margin-bottom: 0;
}
.setting-profile-summary {
  gap: 12px;
}
.setting-profile-shell .vp-nav-btn,
.setting-profile-shell .vp-hero__actions .vp-nav-btn {
  width: 38px;
  height: 38px;
  border-radius: 14px;
}

.setting-profile-shell .vp-nav-btn--back {
  left: 12px;
  top: 12px;
}

.setting-profile-shell .vp-hero__actions {
  right: 12px;
  top: 12px;
}

.setting-profile-shell .vp-avatar-wrap {
  left: 18px;
  bottom: -32px;
  width: 88px;
  height: 88px;
}
.setting-profile-content {
  margin-top: 12px;
  padding: 54px 14px 18px;
  border-radius: 24px;
}

.setting-profile-shell .vp-name {
  font-size: 1.55rem;
}

.setting-profile-shell .update-inffo {
  font-size: 0.92rem;
}

.setting-profile-shell .ep-group {
  padding: 0px 0px 0px;
  border-radius: 20px;
}

.setting-profile-shell .ep-section__title {
  font-size: 1.05rem;
  margin-bottom: 14px;
}

.setting-profile-shell .ep-grid-row {
  grid-template-columns: 1fr;
}

.setting-profile-shell .vp-logout-wrap {
  margin-top: 22px;
}

.setting-profile-shell .vp-logout-btn {

  min-width: 0;
}
}

 @media (max-width: 576px) {
  .setting-profile-shell .vp-logout-btn {
    min-height: 40px !important;
    border-radius: 12px;
    width: 100%;
}
  .setting-profile-shell .vp-logout-wrap {
    margin-top: -55px !important;
}
    
}
@media (max-width: 575px) {
  .setting-profile-page {
    padding: 0px 0px 28px;
  }

  .setting-profile-shell .vp-hero {
    min-height: 184px;
  }

  .setting-profile-shell .vp-avatar-wrap {
    left: 14px;
    width: 82px;
    height: 82px;
  }

  .setting-profile-content {
    padding: 52px 12px 16px;
  }

  .setting-profile-shell .vp-name {
    font-size: 1.35rem;
  }

  .setting-profile-shell .vp-email {
    font-size: 0.92rem;
  }

  .setting-profile-shell .vp-avatar-status {
    max-width: calc(100vw - 72px);
    white-space: normal;
    text-align: center;
    line-height: 1.3;
  }

  .setting-profile-shell .ep-select__chips {
    flex-wrap: wrap;
    overflow-x: visible;
    white-space: normal;
    padding-right: 24px;
  }

  .setting-profile-shell .ep-select__chip {
    max-width: 100%;
    white-space: normal;
    line-height: 1.25;
    text-align: left;
  }
  .formContainer {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 5px;
  }
  .setting-profile-shell .ep-section > .ep-group:last-child .formContainer {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  .setting-profile-shell .vp-logout-wrap {
    margin-top: 0px!important;
      }
  .vp-logout-wrap {
      padding: 18px 0px 16px!important;
  }
  .setting-profile-shell .vp-location-chip {
      padding: 8px 16px;
      margin-bottom: 10px;
  }
}
@media (max-width: 420px) {
  .setting-profile-shell .ep-phone-row {
    flex-direction: column;
    align-items: stretch;
  }

  .setting-profile-shell .ep-phone-code {
    width: 100%;
    min-width: 0;
    justify-content: flex-start;
    padding: 0 14px;
    gap: 8px;
  }
}

      `}</style>
      <Layout showHeader={false}>
        <div className="vp-page setting-profile-page">
          <div className="vp-shell setting-profile-shell">
            <div className="vp-hero">
              <div className="vp-hero__overlay" />

              <button
                type="button"
                className="vp-nav-btn vp-nav-btn--back"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <BackSVG />
              </button>

              <div className="vp-hero__actions">
                <button
                  type="button"
                  className={`vp-nav-btn vp-nav-btn--language${currentPreferredLanguage === "HI" ? " is-hi" : ""}${isLanguageUpdatePending ? " is-loading" : ""}`}
                  aria-label={`Switch language to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`}
                  title={`Current language: ${currentPreferredLanguage}`}
                  onClick={handleLanguageToggle}
                  disabled={isLanguageUpdatePending}
                >
                  <LanguageIconBlack />
                </button>
              </div>

              <div
                className="vp-hero__bg"
                style={{
                  backgroundImage: `url(${profile?.state_image || bgImage})`,
                }}
              />

              <div className="vp-avatar-wrap">
                <img
                  className="vp-avatar"
                  src={activeProfileImage}
                  alt={profile?.name || "Profile"}
                />
                {selectedProfilePicture ? (
                  <>
                    <button
                      type="button"
                      className="vp-avatar-remove"
                      onClick={handleRemoveSelectedProfilePicture}
                      aria-label="Remove selected profile picture"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                    <span className="vp-avatar-status">New image selected</span>
                  </>
                ) : null}
                <button
                  type="button"
                  className="vp-avatar-edit"
                  onClick={() => profilePictureInputRef.current?.click()}
                  aria-label="Change profile picture"
                >
                  <i className="bi bi-camera-fill" />
                </button>
                <input
                  ref={profilePictureInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  hidden
                />
              </div>
            </div>

            <div className="vp-content setting-profile-content">
              <div className="setting-profile-summary">
                <div className="vp-user setting-profile-summary__copy">
                  <h1 className="vp-name blackText">
                    {profile?.name || "User"}
                  </h1>
                  <p className="vp-email text-purple">{profile?.email || ""}</p>
                  <div className="update-inffo grayText">
                    Update your account, address and personal details in one
                    place.
                  </div>
                </div>

                <div className="vp-location-row">
                  <span className="vp-location-chip blackText">
                    <i className="bi bi-geo-alt-fill text-purple" />
                    {profile?.state_name || "State not selected"}
                  </span>
                </div>
              </div>

              <div className="editprofile-content setting-profile-formCard">
                <section className="ep-section">
                  {dynamicProfileSections.map((section) => {
                    if (section.title === "Address") {
                      const [
                        stateField,
                        cityField,
                        pinCodeField,
                        addressField,
                      ] = section.items;

                      return (
                        <div
                          className="ep-group ep-group--full"
                          key={section.title}
                        >
                          <h2 className="ep-section__title blackText">
                            {section.title}
                          </h2>
                          <div className="formContainer">
                            <div className="ep-field">
                              <label
                                className="ep-field__label"
                                htmlFor={stateField.id}
                              >
                                {stateField.label}
                              </label>
                              {renderFieldControl(stateField)}
                            </div>

                            {/* <div className="ep-grid-row"> */}
                            <div className="ep-field">
                              <label
                                className="ep-field__label"
                                htmlFor={cityField.id}
                              >
                                {cityField.label}
                              </label>
                              {renderFieldControl(cityField)}
                            </div>

                            <div className="ep-field">
                              <label
                                className="ep-field__label"
                                htmlFor={pinCodeField.id}
                              >
                                {pinCodeField.label}
                              </label>
                              {renderFieldControl(pinCodeField)}
                            </div>
                            {/* </div> */}

                            <div className="ep-field">
                              <label
                                className="ep-field__label"
                                htmlFor={addressField.id}
                              >
                                {addressField.label}
                              </label>
                              {renderFieldControl(addressField)}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        className={`ep-group${section.title === "Education & Qualification" ? " ep-group--full" : ""}`}
                        key={section.title}
                      >
                        <h2 className="ep-section__title blackText">
                          {section.title}
                        </h2>
                        <div className="formContainer other-details-container">
                          {section.items.map((item) => (
                            <div className="ep-field" key={item.id}>
                              <label
                                className="ep-field__label"
                                htmlFor={item.id}
                              >
                                {item.label}
                              </label>

                              {renderFieldControl(item)}
                            </div>
                          ))}

                          {section.title === "Other Details" && (
                            <div className="other-details-save">
                              <button
                                type="button"
                                className="setting-profile-save"
                                onClick={handleSave}
                                disabled={updateProfileMutation.isPending}
                              >
                                {updateProfileMutation.isPending
                                  ? "Saving..."
                                  : "Save Changes"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              </div>
              {/* <div className="setting-profile-actions">
                <button
                  type="button"
                  className="setting-profile-save"
                  onClick={handleSave}
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </button>
              </div> */}
            </div>
          </div>
        </div>

        <MyWalletSidebar withTopOffset={false} />
      </Layout>
    </>
  );
};

export default EditProfile;
